import { AppThunkAction } from '../reducers';

// Actions
import * as Actions from '../actions';

// Services
import * as service from '../../services/ProtocolConsumptionConceptValues.Services';

// Models
import { ProtocolConsumptionConceptValues } from '../../models/Entities/Protocols/ProtocolConsumptionConceptValues';
import { NewProtocolConsumptionConceptValue } from '../../models/Entities/Protocols/NewProtocolConsumptionConceptValue';
import { ErrorType } from '../../models/HttpError';

type KnownAction =
    // get all
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueGetAllRequestAction
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueGetAllSuccessAction
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueGetAllFailureAction
    // create
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueCreateRequestAction
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueCreateSuccessAction
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueCreateFailureAction
    // update
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueUpdateRequestAction
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueUpdateSuccessAction
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueUpdateFailureAction
    // delete
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueDeleteRequestAction
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueDeleteSuccessAction
    | Actions.ProtocolConsumptionConceptValues.ProtocolConsumptionConceptValueDeleteFailureAction;

const NotAuthorized: ErrorType = {
    ErrorCode: 401,
    ErrorMessage: 'Not authorized',
    Errors: []
};

export const ActionCreators = {

    GetAllConceptValuesByConsumption: (protocolId: number, consumptionId: string): AppThunkAction<KnownAction> =>
        (dispatch, getState) => {
            const appState = getState();

            if (appState.oidc?.user?.access_token === undefined) {
                dispatch({
                    type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_GETALL_FAILURE',
                    protocolId,
                    consumptionId,
                    error: NotAuthorized
                });
                return;
            }

            const token: string = appState.oidc.user.access_token;

            dispatch({
                type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_GETALL_REQUEST',
                protocolId,
                consumptionId
            });

            service.GetAllConceptValuesByConsumptionId(token, consumptionId)
                .then((conceptValues: ProtocolConsumptionConceptValues[]) => {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_GETALL_SUCCESS',
                        protocolId,
                        consumptionId,
                        conceptValues: conceptValues ?? []
                    });
                })
                .catch((error: ErrorType) => {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_GETALL_FAILURE',
                        protocolId,
                        consumptionId,
                        error
                    });
                });
        },

    CreateConceptValue: (protocolId: number, conceptValue: NewProtocolConsumptionConceptValue): AppThunkAction<KnownAction> =>
        (dispatch, getState) => {
            const appState = getState();
            const consumptionId: string = conceptValue.protocolConsumedServiceId;

            if (appState.oidc?.user?.access_token === undefined) {
                dispatch({
                    type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_CREATE_FAILURE',
                    protocolId,
                    consumptionId,
                    error: NotAuthorized
                });
                return;
            }

            const token: string = appState.oidc.user.access_token;

            dispatch({
                type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_CREATE_REQUEST',
                protocolId,
                consumptionId
            });

            service.CreateConceptValue(token, conceptValue)
                .then((created: ProtocolConsumptionConceptValues) => {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_CREATE_SUCCESS',
                        protocolId,
                        consumptionId,
                        conceptValue: created
                    });
                })
                .catch((error: ErrorType) => {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_CREATE_FAILURE',
                        protocolId,
                        consumptionId,
                        error
                    });
                });
        },

    UpdateConceptValue: (protocolId: number, conceptValue: NewProtocolConsumptionConceptValue & { id: number }): AppThunkAction<KnownAction> =>
        (dispatch, getState) => {
            const appState = getState();
            const consumptionId: string = conceptValue.protocolConsumedServiceId;

            if (appState.oidc?.user?.access_token === undefined) {
                dispatch({
                    type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_UPDATE_FAILURE',
                    protocolId,
                    consumptionId,
                    conceptValueId: conceptValue.id,
                    error: NotAuthorized
                });
                return;
            }

            const token: string = appState.oidc.user.access_token;

            dispatch({
                type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_UPDATE_REQUEST',
                protocolId,
                consumptionId,
                conceptValueId: conceptValue.id
            });

            service.UpdateConceptValue(token, conceptValue)
                .then((updated: ProtocolConsumptionConceptValues) => {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_UPDATE_SUCCESS',
                        protocolId,
                        consumptionId,
                        conceptValueId: conceptValue.id,
                        // El PUT puede responder sin cuerpo util; en ese caso se
                        // conserva lo que se acaba de enviar para no vaciar la fila.
                        conceptValue: updated ?? ({ ...conceptValue } as unknown as ProtocolConsumptionConceptValues)
                    });
                })
                .catch((error: ErrorType) => {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_UPDATE_FAILURE',
                        protocolId,
                        consumptionId,
                        conceptValueId: conceptValue.id,
                        error
                    });
                });
        },

    DeleteConceptValue: (protocolId: number, consumptionId: string, conceptValueId: number): AppThunkAction<KnownAction> =>
        (dispatch, getState) => {
            const appState = getState();

            if (appState.oidc?.user?.access_token === undefined) {
                dispatch({
                    type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_DELETE_FAILURE',
                    protocolId,
                    consumptionId,
                    conceptValueId,
                    error: NotAuthorized
                });
                return;
            }

            const token: string = appState.oidc.user.access_token;

            dispatch({
                type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_DELETE_REQUEST',
                protocolId,
                consumptionId,
                conceptValueId
            });

            service.DeleteConceptValue(token, conceptValueId)
                .then(() => {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_DELETE_SUCCESS',
                        protocolId,
                        consumptionId,
                        conceptValueId
                    });
                })
                .catch((error: ErrorType) => {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONCONCEPTVALUE_DELETE_FAILURE',
                        protocolId,
                        consumptionId,
                        conceptValueId,
                        error
                    });
                });
        },
};
